export const DASHBOARD_WIDGET_IDS = [
  'alertTypes',
  'devicesStatus',
  'driversRank',
  'vehiclesDistance',
  'driversDistance',
  'driversAlertsCount',
] as const;

export type DashboardWidgetId = typeof DASHBOARD_WIDGET_IDS[number];

export type DashboardLayout = {
  order: DashboardWidgetId[];
  hidden: DashboardWidgetId[];
};

export const DASHBOARD_GRID_COLUMNS = 4;
export const DASHBOARD_GRID_GAP = 20;
export const DASHBOARD_ROW_HEIGHT = 205;
export const DASHBOARD_MIN_COLUMN_WIDTH = 230;

export type DashboardColumnSpan = 1 | 2 | 3 | 4;
export type DashboardRowSpan = 1 | 2 | 3;

export type DashboardWidgetDefinition = {
  label: string;
  columnSpan: DashboardColumnSpan;
  rowSpan: DashboardRowSpan;
  hasInternalMenu: boolean;
};

export const DASHBOARD_WIDGETS: Record<DashboardWidgetId, DashboardWidgetDefinition> = {
  alertTypes: {
    label: 'Sensor types',
    columnSpan: 2,
    rowSpan: 2,
    hasInternalMenu: true,
  },
  devicesStatus: {
    label: 'Devices status',
    columnSpan: 2,
    rowSpan: 2,
    hasInternalMenu: true,
  },
  driversRank: {
    label: 'Drivers rank',
    columnSpan: 1,
    rowSpan: 2,
    hasInternalMenu: true,
  },
  vehiclesDistance: {
    label: 'Vehicle distance',
    columnSpan: 1,
    rowSpan: 2,
    hasInternalMenu: false,
  },
  driversDistance: {
    label: 'Driver distance',
    columnSpan: 1,
    rowSpan: 2,
    hasInternalMenu: false,
  },
  driversAlertsCount: {
    label: 'Driver alerts',
    columnSpan: 1,
    rowSpan: 2,
    hasInternalMenu: false,
  },
};

export const DEFAULT_DASHBOARD_LAYOUT: DashboardLayout = {
  order: [...DASHBOARD_WIDGET_IDS],
  hidden: [],
};

const isDashboardWidgetId = (value: unknown): value is DashboardWidgetId => (
  typeof value === 'string' && DASHBOARD_WIDGET_IDS.includes(value as DashboardWidgetId)
);

const uniqueWidgetIds = (value: unknown): DashboardWidgetId[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce<DashboardWidgetId[]>((result, item) => {
    if (isDashboardWidgetId(item) && !result.includes(item)) {
      result.push(item);
    }
    return result;
  }, []);
};

export const normalizeDashboardLayout = (value: unknown): DashboardLayout => {
  if (!value || typeof value !== 'object') {
    return DEFAULT_DASHBOARD_LAYOUT;
  }

  const stored = value as Partial<DashboardLayout>;
  const hidden = uniqueWidgetIds(stored.hidden);
  const order = uniqueWidgetIds(stored.order);

  DASHBOARD_WIDGET_IDS.forEach((id) => {
    if (!order.includes(id)) {
      order.push(id);
    }
  });

  return { order, hidden };
};

export const moveDashboardWidget = (
  layout: DashboardLayout,
  activeId: DashboardWidgetId,
  overId: DashboardWidgetId,
): DashboardLayout => {
  if (activeId === overId) {
    return layout;
  }

  const visibleOrder = layout.order.filter((id) => !layout.hidden.includes(id));
  const fromIndex = visibleOrder.indexOf(activeId);
  const toIndex = visibleOrder.indexOf(overId);

  if (fromIndex < 0 || toIndex < 0) {
    return layout;
  }

  const reorderedVisible = [...visibleOrder];
  const [movedWidget] = reorderedVisible.splice(fromIndex, 1);
  reorderedVisible.splice(toIndex, 0, movedWidget);

  let visibleIndex = 0;
  const order = layout.order.map((id) => {
    if (layout.hidden.includes(id)) {
      return id;
    }

    const visibleWidgetId = reorderedVisible[visibleIndex];
    visibleIndex += 1;
    return visibleWidgetId;
  });

  return { ...layout, order };
};

export const removeDashboardWidget = (
  layout: DashboardLayout,
  widgetId: DashboardWidgetId,
): DashboardLayout => (
  layout.hidden.includes(widgetId)
    ? layout
    : { ...layout, hidden: [...layout.hidden, widgetId] }
);

export const addDashboardWidget = (
  layout: DashboardLayout,
  widgetId: DashboardWidgetId,
): DashboardLayout => ({
  ...layout,
  hidden: layout.hidden.filter((id) => id !== widgetId),
});
